import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, FileText, LayoutTemplate } from 'lucide-react';
import { Badge, EmptyState } from './ui';
import { TemplatePreview } from './TemplatePreview';
import { templateDisplayName } from '../lib/templateDisplay';
import type { Template } from '../types';

/**
 * Zentraler Vorlagen-Galerie-Dialog (ersetzt den alten Zwei-Klick-Picker).
 *
 * Ein Modal im WhatsApp-Business-Stil: links eine durchsuchbare Liste, rechts
 * eine Live-Vorschau der hervorgehobenen Vorlage als Chat-Bubble. Ein Klick auf
 * ein Item (oder Enter) wählt die Vorlage SOFORT (onPick) und schließt — kein
 * zweiter Auswahlschritt.
 *
 * Die Vorlagen werden vom Aufrufer übergeben (bereits gefiltert, z.B. nur
 * APPROVED). Tastatur: ↑/↓ bewegt die Auswahl, Enter wählt, Esc schließt.
 */

interface TemplateGalleryDialogProps {
  open: boolean;
  templates: Template[];
  onClose: () => void;
  onPick: (t: Template) => void;
}

export function TemplateGalleryDialog({
  open,
  templates,
  onClose,
  onPick,
}: TemplateGalleryDialogProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Beim Öffnen Suche zurücksetzen, das auslösende Element merken und Fokus ins
  // Suchfeld setzen. Beim Schließen kehrt der Fokus dorthin zurück (a11y, analog
  // zu ConversationActionsMenu).
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    const id = setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      clearTimeout(id);
      restoreFocusRef.current?.focus();
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => {
      const haystack = `${templateDisplayName(t)} ${t.name} ${t.body}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [templates, query]);

  // activeIndex in Grenzen halten, wenn sich die Filterung ändert.
  useEffect(() => {
    setActiveIndex((i) => Math.min(Math.max(0, i), Math.max(0, filtered.length - 1)));
  }, [filtered.length]);

  const active = filtered[activeIndex] ?? null;

  // Esc schließt — Propagation stoppen, damit ein darunterliegendes Modal nicht mitschließt.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  // Aktives Item in den sichtbaren Bereich scrollen.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLElement>(`[data-idx="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!open) return null;

  function choose(t: Template) {
    onPick(t);
    onClose();
  }

  function onListKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (active) choose(active);
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center bg-black/40 p-6 pt-16"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="template-gallery-title"
        className="flex h-[34rem] max-h-[80vh] w-full max-w-[640px] flex-col overflow-hidden rounded-xl bg-white shadow-brand-lg"
      >
        <header className="flex items-center justify-between border-b border-kalkuel-border px-5 py-3">
          <h2
            id="template-gallery-title"
            className="flex items-center gap-2 text-sm font-semibold text-kalkuel-primary"
          >
            <LayoutTemplate size={16} className="text-kalkuel-accent2" />
            Vorlage wählen
          </h2>
          <button
            type="button"
            onClick={onClose}
            title="Schließen"
            aria-label="Schließen"
            className="rounded-pill p-1 text-kalkuel-muted hover:bg-kalkuel-surface hover:text-kalkuel-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-kalkuel-accent/40"
          >
            <X size={16} />
          </button>
        </header>

        <div className="flex min-h-0 flex-1">
          {/* LINKS: suchbare Liste */}
          <div className="flex w-1/2 min-w-0 flex-col border-r border-kalkuel-border">
            <div className="border-b border-kalkuel-border p-3">
              <div className="relative">
                <Search
                  size={14}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-kalkuel-muted"
                />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onListKeyDown}
                  placeholder="Vorlage suchen…"
                  aria-label="Vorlage suchen"
                  className="w-full rounded-md border border-kalkuel-border py-2 pl-8 pr-3 text-sm focus:border-kalkuel-accent focus:outline-none focus:ring-2 focus:ring-kalkuel-accent/30"
                />
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="flex flex-1 items-center justify-center">
                <EmptyState
                  icon={<FileText size={20} />}
                  title={templates.length === 0 ? 'Keine Vorlagen' : 'Keine Treffer'}
                  description={
                    templates.length === 0
                      ? 'Es sind noch keine Vorlagen vorhanden.'
                      : 'Keine Vorlage passt zur Suche.'
                  }
                />
              </div>
            ) : (
              <ul
                ref={listRef}
                role="listbox"
                aria-label="Vorlagen"
                className="flex-1 overflow-y-auto py-1"
                onKeyDown={onListKeyDown}
              >
                {filtered.map((t, idx) => {
                  const isActive = idx === activeIndex;
                  return (
                    <li key={t.id} data-idx={idx} role="option" aria-selected={isActive}>
                      <button
                        type="button"
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => choose(t)}
                        className={[
                          'flex w-full flex-col gap-1 border-l-2 px-4 py-2.5 text-left transition-colors focus:outline-none',
                          isActive
                            ? 'border-kalkuel-accent bg-kalkuel-accent-soft'
                            : 'border-transparent hover:bg-kalkuel-surface',
                        ].join(' ')}
                      >
                        <span className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-semibold text-kalkuel-primary">
                            {templateDisplayName(t)}
                          </span>
                          <span className="flex shrink-0 items-center gap-1">
                            {/* Tone passt jeweils zum eigenen Text: Kanal (accent) + Status nur, wenn nicht freigegeben. */}
                            <Badge tone="accent">{t.channel === 'WHATSAPP' ? 'WA' : 'E-Mail'}</Badge>
                            {t.metaStatus !== 'APPROVED' && (
                              <Badge tone={STATUS_TONE[t.metaStatus] ?? 'neutral'}>
                                {STATUS_LABEL[t.metaStatus] ?? t.metaStatus}
                              </Badge>
                            )}
                          </span>
                        </span>
                        <span className="line-clamp-2 text-xs text-kalkuel-muted">
                          {snippet(t.body)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* RECHTS: Live-Vorschau */}
          <div className="flex w-1/2 min-w-0 flex-col bg-kalkuel-surface">
            <div className="border-b border-kalkuel-border px-4 py-2">
              <span className="kalkuel-eyebrow">Vorschau</span>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {active ? (
                <TemplatePreview
                  template={{
                    displayName: templateDisplayName(active),
                    body: active.body,
                    headerType: active.headerType,
                    headerText: active.headerText,
                    headerMediaFilename: mediaFilename(active.headerMediaSampleUrl),
                    footer: active.footer,
                    buttons: active.buttons,
                    variableDefs: active.variableDefs,
                  }}
                />
              ) : (
                <p className="text-xs text-kalkuel-muted">Keine Vorlage ausgewählt.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const STATUS_TONE: Record<string, 'success' | 'warning' | 'danger' | 'neutral' | 'info'> = {
  APPROVED: 'success',
  PENDING: 'warning',
  REJECTED: 'danger',
  DRAFT: 'neutral',
};

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'In Prüfung',
  REJECTED: 'Abgelehnt',
  DRAFT: 'Entwurf',
};

/** Erste Zeile(n) des Bodys als kurzes, einzeiliges Snippet. */
function snippet(body: string): string {
  return (body ?? '').replace(/\s+/g, ' ').trim() || '(kein Text)';
}

/**
 * Zieht den reinen Dateinamen aus einer headerMediaSampleUrl (`/api/media/:id`)
 * für die Vorschau. Liefert null, wenn nichts hinterlegt ist.
 */
function mediaFilename(sampleUrl: string | null | undefined): string | null {
  if (!sampleUrl) return null;
  const tail = sampleUrl.split('/').filter(Boolean).pop();
  return tail ? `Medium: ${tail}` : 'Medien-Beispiel';
}
